import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  FORUMS, FORUM_POSTS, TRENDING_FORUM_POSTS, MY_FORUM_POSTS, SAVED_FORUM_POSTS,
  CREATE_FORUM, CREATE_FORUM_POST, DELETE_FORUM_POST, REPLY_TO_FORUM_POST,
  DELETE_FORUM_REPLY, VOTE_FORUM_POST, VOTE_FORUM_REPLY, SAVE_FORUM_POST, MARK_REPLY_SOLUTION,
} from '../graphql';
import {
  MessagesSquare, Plus, X, Send, Trash2, Search, ChevronUp, ChevronDown,
  Bookmark, Pin, CheckCircle2, Flame, Globe, ArrowLeft, MessageCircle, User as UserIcon,
  HelpCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  { value: 'GENERAL',       label: 'General',       emoji: '💬', color: 'bg-gray-100 text-gray-700' },
  { value: 'ACADEMIC',      label: 'Academic',      emoji: '📚', color: 'bg-blue-100 text-blue-700' },
  { value: 'PLACEMENT',     label: 'Placement',     emoji: '💼', color: 'bg-amber-100 text-amber-700' },
  { value: 'HOSTEL',        label: 'Hostel',        emoji: '🏠', color: 'bg-orange-100 text-orange-700' },
  { value: 'CAMPUS',        label: 'Campus',        emoji: '🏛️', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'TECH',          label: 'Tech',          emoji: '💻', color: 'bg-violet-100 text-violet-700' },
  { value: 'CAREER',        label: 'Career',        emoji: '🚀', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'MENTAL_HEALTH', label: 'Mental Health', emoji: '🧠', color: 'bg-pink-100 text-pink-700' },
  { value: 'SPORTS',        label: 'Sports',        emoji: '⚽', color: 'bg-green-100 text-green-700' },
  { value: 'MISC',          label: 'Misc',          emoji: '🎲', color: 'bg-slate-100 text-slate-600' },
];
const catCfg = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[0];

const fmtDate = (v) => {
  const d = new Date(isNaN(Number(v)) ? v : Number(v));
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const authorName = (a, isAnonymous) => (isAnonymous || !a ? 'Anonymous' : a.name);

/* ── Vote arrows (single-upvote toggle: up adds, down removes) ──────────────── */
function VoteArrows({ score, hasVoted, onVote, compact }) {
  return (
    <div className={`flex flex-col items-center flex-shrink-0 ${compact ? 'gap-0' : 'gap-0.5'}`}>
      <button onClick={onVote} title={hasVoted ? 'Remove upvote' : 'Upvote'}
        className={`p-0.5 rounded transition-colors ${hasVoted ? 'text-indigo-600' : 'text-gray-300 hover:text-indigo-500'}`}>
        <ChevronUp size={compact ? 15 : 19} strokeWidth={3} />
      </button>
      <span className={`font-bold leading-none ${compact ? 'text-xs' : 'text-sm'} ${hasVoted ? 'text-indigo-600' : 'text-gray-600'}`}>{score}</span>
      <button onClick={() => hasVoted && onVote()} disabled={!hasVoted} title={hasVoted ? 'Remove upvote' : ''}
        className={`p-0.5 rounded transition-colors ${hasVoted ? 'text-gray-400 hover:text-red-400' : 'text-gray-200 cursor-default'}`}>
        <ChevronDown size={compact ? 15 : 19} strokeWidth={3} />
      </button>
    </div>
  );
}

/* ── Single reply row ───────────────────────────────────────────────────────── */
function ReplyItem({ reply, currentUserId, onVote, onDelete, onMarkSolution, isPostAuthor, isQuestionPost }) {
  const isOwn = reply.author?.id === currentUserId;
  return (
    <div className="flex gap-2.5 group/reply">
      <VoteArrows compact score={reply.upvotes} hasVoted={reply.hasVoted} onVote={() => onVote(reply.id)} />
      <div className={`flex-1 rounded-xl px-3 py-2 ${reply.isSolution ? 'bg-green-50 border border-green-100' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-0.5">
          <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
            {reply.isAnonymous || !reply.author
              ? <span className="flex items-center gap-1 text-gray-400"><UserIcon size={11}/> Anonymous</span>
              : <>{reply.author.name}{reply.author.college && <span className="font-normal text-gray-400">· {reply.author.college}</span>}</>}
            {reply.isSolution && <span className="flex items-center gap-0.5 text-green-600"><CheckCircle2 size={12}/> Accepted Answer</span>}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-300">{fmtDate(reply.createdAt)}</span>
            {isPostAuthor && isQuestionPost && (
              <button onClick={() => onMarkSolution(reply.id, !reply.isSolution)}
                title={reply.isSolution ? 'Unmark as answer' : 'Mark as accepted answer'}
                className={`opacity-0 group-hover/reply:opacity-100 transition-all text-xs flex items-center gap-0.5 ${reply.isSolution ? 'text-green-500 hover:text-red-400' : 'text-gray-300 hover:text-green-500'}`}>
                <CheckCircle2 size={13}/>
              </button>
            )}
            {isOwn && (
              <button onClick={() => onDelete(reply.id)} title="Delete reply"
                className="opacity-0 group-hover/reply:opacity-100 text-gray-300 hover:text-red-400 transition-all">
                <Trash2 size={12}/>
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 whitespace-pre-wrap">{reply.body}</p>
      </div>
    </div>
  );
}

/* ── Post card (collapsed preview → expanded full body + replies) ───────────── */
function PostCard({ post, currentUserId, showForum, onVotePost, onVoteReply, onSave, onDeletePost, onDeleteReply, onReply, onMarkSolution }) {
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyAnon, setReplyAnon] = useState(false);
  const [sending, setSending] = useState(false);
  const isOwn = post.author?.id === currentUserId;
  const cat = catCfg(post.forum?.category);
  const preview = post.body.length > 180 && !expanded ? post.body.slice(0, 180) + '…' : post.body;

  const submitReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try { await onReply(post.id, replyText.trim(), replyAnon); setReplyText(''); setReplyAnon(false); }
    finally { setSending(false); }
  };

  return (
    <div className="card !p-4 hover:shadow-md transition-shadow group">
      <div className="flex gap-3">
        <VoteArrows score={post.upvotes} hasVoted={post.hasVoted} onVote={() => onVotePost(post.id)} />
        <div className="flex-1 min-w-0">
          {/* Meta row */}
          <div className="flex items-center gap-2 flex-wrap mb-1 text-xs text-gray-400">
            {showForum && post.forum && (
              <span className={`px-2 py-0.5 rounded-full font-medium ${cat.color}`}>{post.forum.icon} {post.forum.name}</span>
            )}
            {post.isQuestion && <span className="flex items-center gap-0.5 text-blue-600 font-medium"><HelpCircle size={11}/> Question</span>}
            {post.isPinned && <span className="flex items-center gap-0.5 text-amber-600 font-medium"><Pin size={11}/> Pinned</span>}
            {post.isSolved && <span className="flex items-center gap-0.5 text-green-600 font-medium"><CheckCircle2 size={11}/> Answered</span>}
            <span>{authorName(post.author, post.isAnonymous)}{!post.isAnonymous && post.author?.college ? ` · ${post.author.college}` : ''}</span>
            <span>· {fmtDate(post.createdAt)}</span>
          </div>

          {/* Title + body */}
          <button onClick={() => setExpanded(e => !e)} className="text-left w-full">
            <h3 className="font-bold text-gray-900 leading-snug">{post.title}</h3>
          </button>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed whitespace-pre-wrap">{preview}</p>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {post.tags.map(t => <span key={t} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">#{t}</span>)}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 pt-3 mt-2 border-t border-gray-50">
            <button onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-indigo-500 transition-colors">
              <MessageCircle size={14}/> {post.replyCount} {post.replyCount === 1 ? 'reply' : 'replies'}
            </button>
            <button onClick={() => onSave(post.id)} title={post.hasSaved ? 'Unsave' : 'Save'}
              className={`flex items-center gap-1.5 text-sm transition-colors ${post.hasSaved ? 'text-violet-600' : 'text-gray-400 hover:text-violet-500'}`}>
              <Bookmark size={14} fill={post.hasSaved ? 'currentColor' : 'none'}/> {post.hasSaved ? 'Saved' : 'Save'}
            </button>
            <span className="text-xs text-gray-300 ml-auto">{post.views} views</span>
            {isOwn && (
              <button onClick={() => onDeletePost(post.id)} title="Delete post"
                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all">
                <Trash2 size={14}/>
              </button>
            )}
          </div>

          {/* Expanded: replies + composer */}
          {expanded && (
            <div className="mt-3 space-y-2.5">
              {post.replies?.length === 0 && <p className="text-xs text-gray-400 italic">{post.isQuestion ? 'No answers yet — be the first to answer.' : 'No replies yet — be the first to reply.'}</p>}
              {post.replies?.map(r => (
                <ReplyItem key={r.id} reply={r} currentUserId={currentUserId} onVote={onVoteReply} onDelete={onDeleteReply}
                  onMarkSolution={onMarkSolution} isPostAuthor={isOwn} isQuestionPost={post.isQuestion} />
              ))}
              <div className="flex items-center gap-2 pt-1">
                <input className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:border-indigo-400"
                  value={replyText} onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitReply()} placeholder={post.isQuestion ? 'Write your answer…' : 'Write a reply…'} />
                <label className="flex items-center gap-1 text-[11px] text-gray-400 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" checked={replyAnon} onChange={e => setReplyAnon(e.target.checked)} className="accent-indigo-600" /> Anon
                </label>
                <button onClick={submitReply} disabled={sending || !replyText.trim()}
                  className="text-indigo-500 hover:text-indigo-700 disabled:opacity-30"><Send size={16}/></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Shared post list with loading / error / empty states ───────────────────── */
function PostList({ loading, error, posts, empty, showForum, handlers, currentUserId }) {
  if (loading) return <div className="text-center py-16 text-gray-400 text-sm">Loading posts…</div>;
  if (error) return <div className="card !p-5 text-center text-sm text-red-500">Couldn't load posts: {error.message}</div>;
  if (!posts.length) return <div className="text-center py-16"><div className="text-5xl mb-3">🗨️</div><p className="text-gray-500 text-sm">{empty}</p></div>;
  return (
    <div className="space-y-3">
      {posts.map(p => <PostCard key={p.id} post={p} currentUserId={currentUserId} showForum={showForum} {...handlers} />)}
    </div>
  );
}

const TABS = [
  { key: 'forums',   label: 'Forums',   icon: <MessagesSquare size={13}/> },
  { key: 'trending', label: 'Trending', icon: <Flame size={13}/> },
  { key: 'mine',     label: 'My Posts', icon: <UserIcon size={13}/> },
  { key: 'saved',    label: 'Saved',    icon: <Bookmark size={13}/> },
];

export default function ForumsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('forums');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedForum, setSelectedForum] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [showCreateForum, setShowCreateForum] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [forumForm, setForumForm] = useState({ name: '', description: '', category: 'GENERAL', isGlobal: false });
  const [postForm, setPostForm] = useState({ title: '', body: '', tagsInput: '', isAnonymous: false, isQuestion: false });

  /* Queries */
  const forumsQ = useQuery(FORUMS, { variables: { category: categoryFilter || undefined } });
  const postsQ = useQuery(FORUM_POSTS, {
    variables: { forumId: selectedForum?.id, search: search || undefined, sortBy: sortBy || undefined },
    skip: !selectedForum,
  });
  const trendingQ = useQuery(TRENDING_FORUM_POSTS, { skip: tab !== 'trending' });
  const mineQ = useQuery(MY_FORUM_POSTS, { skip: tab !== 'mine' });
  const savedQ = useQuery(SAVED_FORUM_POSTS, { skip: tab !== 'saved' });

  /* Mutations — refetch every active query so lists, counts & vote state stay fresh */
  const opts = { refetchQueries: 'active' };
  const [createForum, { loading: creatingForum, error: createForumErr }] = useMutation(CREATE_FORUM, {
    ...opts,
    onCompleted: (d) => { setShowCreateForum(false); setForumForm({ name: '', description: '', category: 'GENERAL', isGlobal: false }); if (d?.createForum) setSelectedForum(d.createForum); },
  });
  const [createPost, { loading: creatingPost, error: createPostErr }] = useMutation(CREATE_FORUM_POST, {
    ...opts,
    onCompleted: () => { setShowCreatePost(false); setPostForm({ title: '', body: '', tagsInput: '', isAnonymous: false, isQuestion: false }); },
  });
  const [deletePost] = useMutation(DELETE_FORUM_POST, opts);
  const [replyToPost] = useMutation(REPLY_TO_FORUM_POST, opts);
  const [deleteReply] = useMutation(DELETE_FORUM_REPLY, opts);
  const [votePost] = useMutation(VOTE_FORUM_POST, opts);
  const [voteReply] = useMutation(VOTE_FORUM_REPLY, opts);
  const [savePost] = useMutation(SAVE_FORUM_POST, opts);
  const [markSolution] = useMutation(MARK_REPLY_SOLUTION, opts);

  const handlers = {
    onVotePost: (postId) => votePost({ variables: { postId } }),
    onVoteReply: (replyId) => voteReply({ variables: { replyId } }),
    onSave: (postId) => savePost({ variables: { postId } }),
    onDeletePost: (postId) => window.confirm('Delete this post?') && deletePost({ variables: { postId } }),
    onDeleteReply: (replyId) => window.confirm('Delete this reply?') && deleteReply({ variables: { replyId } }),
    onReply: (postId, body, isAnonymous) => replyToPost({ variables: { postId, body, isAnonymous } }),
    onMarkSolution: (replyId, isSolution) => markSolution({ variables: { replyId, isSolution } }),
  };

  const forums = forumsQ.data?.forums || [];

  const openForum = (f) => { setSelectedForum(f); setSearch(''); setSearchInput(''); setSortBy(''); };
  const submitCreateForum = (e) => {
    e.preventDefault();
    createForum({ variables: { name: forumForm.name.trim(), description: forumForm.description.trim() || null, category: forumForm.category, isGlobal: forumForm.isGlobal, college: forumForm.isGlobal ? null : user?.college } });
  };
  const submitCreatePost = (e) => {
    e.preventDefault();
    createPost({ variables: { forumId: selectedForum.id, title: postForm.title.trim(), body: postForm.body.trim(), tags: postForm.tagsInput.split(',').map(s => s.trim()).filter(Boolean), isAnonymous: postForm.isAnonymous, isQuestion: postForm.isQuestion } });
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Discussion Forums</h1>
          <p className="text-gray-500 text-sm">Campus boards · Ask, answer, upvote · Your college, your conversations</p>
        </div>
        {tab === 'forums' && !selectedForum && (
          <button onClick={() => setShowCreateForum(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14}/> Create forum</button>
        )}
        {selectedForum && (
          <button onClick={() => setShowCreatePost(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14}/> New post</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap mb-4">
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSelectedForum(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${tab === t.key && !selectedForum ? 'bg-gray-800 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Forums tab: forum list ─────────────────────────────────────────── */}
      {tab === 'forums' && !selectedForum && (
        <>
          <div className="flex gap-1 flex-wrap mb-4">
            <button onClick={() => setCategoryFilter('')}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!categoryFilter ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-200 text-gray-400 hover:border-indigo-300'}`}>All</button>
            {CATEGORIES.map(c => (
              <button key={c.value} onClick={() => setCategoryFilter(f => f === c.value ? '' : c.value)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${categoryFilter === c.value ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-200 text-gray-400 hover:border-indigo-300'}`}>
                {c.label}
              </button>
            ))}
          </div>

          {forumsQ.loading && <div className="text-center py-16 text-gray-400 text-sm">Loading forums…</div>}
          {forumsQ.error && <div className="card !p-5 text-center text-sm text-red-500">Couldn't load forums: {forumsQ.error.message}</div>}
          {!forumsQ.loading && !forumsQ.error && (
            forums.length === 0
              ? <div className="text-center py-16"><p className="text-gray-500 text-sm">No forums yet — start the first board!</p><button onClick={() => setShowCreateForum(true)} className="btn-primary mt-3 text-sm">+ Create Forum</button></div>
              : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {forums.map(f => {
                    const cat = catCfg(f.category);
                    return (
                      <button key={f.id} onClick={() => openForum(f)} className="card !p-4 text-left hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-2.5 mb-1.5">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: (f.color || '#6366f1') + '22' }}>{f.icon || '💬'}</div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">{f.name}</p>
                            <p className="text-xs text-gray-400">{f.postCount} {f.postCount === 1 ? 'post' : 'posts'}{f.isGlobal ? '' : f.college ? ` · ${f.college}` : ''}</p>
                          </div>
                        </div>
                        {f.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{f.description}</p>}
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.color}`}>{cat.label}</span>
                          {f.isGlobal && <span className="flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium"><Globe size={10}/> Global</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
          )}
        </>
      )}

      {/* ── Selected forum: posts detail ───────────────────────────────────── */}
      {selectedForum && (
        <>
          <div className="card !p-4 mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedForum(null)} className="text-gray-400 hover:text-gray-700 transition-colors" title="Back to forums"><ArrowLeft size={18}/></button>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: (selectedForum.color || '#6366f1') + '22' }}>{selectedForum.icon || '💬'}</div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-gray-900 flex items-center gap-2">{selectedForum.name}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catCfg(selectedForum.category).color}`}>{catCfg(selectedForum.category).label}</span>
                </h2>
                {selectedForum.description && <p className="text-xs text-gray-500 truncate">{selectedForum.description}</p>}
              </div>
            </div>
          </div>

          {/* Search + sort toolbar */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300"/>
              <input className="w-full border border-gray-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                placeholder="Search posts… (press Enter)" value={searchInput}
                onChange={e => { setSearchInput(e.target.value); if (!e.target.value) setSearch(''); }}
                onKeyDown={e => e.key === 'Enter' && setSearch(searchInput.trim())} />
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-600 focus:outline-none focus:border-indigo-400 bg-white">
              <option value="">Top</option>
              <option value="HOT">Hot</option>
              <option value="NEW">New</option>
            </select>
          </div>

          <PostList loading={postsQ.loading} error={postsQ.error} posts={postsQ.data?.forumPosts || []}
            empty={search ? `No posts match "${search}".` : 'No posts in this forum yet — start the discussion!'}
            showForum={false} handlers={handlers} currentUserId={user?.id} />
        </>
      )}

      {/* ── Trending / My posts / Saved tabs ───────────────────────────────── */}
      {tab === 'trending' && !selectedForum && (
        <PostList loading={trendingQ.loading} error={trendingQ.error} posts={trendingQ.data?.trendingForumPosts || []}
          empty="Nothing trending yet — upvotes decide what shows up here." showForum handlers={handlers} currentUserId={user?.id} />
      )}
      {tab === 'mine' && !selectedForum && (
        <PostList loading={mineQ.loading} error={mineQ.error} posts={mineQ.data?.myForumPosts || []}
          empty="You haven't posted yet — pick a forum and share something." showForum handlers={handlers} currentUserId={user?.id} />
      )}
      {tab === 'saved' && !selectedForum && (
        <PostList loading={savedQ.loading} error={savedQ.error} posts={savedQ.data?.savedForumPosts || []}
          empty="No saved posts — tap the bookmark on any post to keep it here." showForum handlers={handlers} currentUserId={user?.id} />
      )}

      {/* ── Create forum modal ─────────────────────────────────────────────── */}
      {showCreateForum && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Create a forum</h2>
              <button onClick={() => setShowCreateForum(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <form onSubmit={submitCreateForum} className="p-5 space-y-4">
              <input required className="input w-full" placeholder="Forum name (e.g. Placements 2027)"
                value={forumForm.name} onChange={e => setForumForm(f => ({ ...f, name: e.target.value }))} />
              <textarea className="input w-full resize-none h-20 text-sm" placeholder="What is this board about? (optional)"
                value={forumForm.description} onChange={e => setForumForm(f => ({ ...f, description: e.target.value }))} />
              <div className="grid grid-cols-5 gap-1">
                {CATEGORIES.map(c => (
                  <button key={c.value} type="button" onClick={() => setForumForm(f => ({ ...f, category: c.value }))}
                    className={`flex flex-col items-center gap-0.5 p-1.5 rounded-xl border-2 transition-colors text-[10px] font-medium ${forumForm.category === c.value ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'}`}>
                    <span className="truncate w-full text-center">{c.label}</span>
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={forumForm.isGlobal} onChange={e => setForumForm(f => ({ ...f, isGlobal: e.target.checked }))} className="accent-indigo-600" />
                <Globe size={13} className="text-sky-500"/> Global — visible to all colleges (otherwise scoped to {user?.college || 'your college'})
              </label>
              {createForumErr && <p className="text-xs text-red-500">{createForumErr.message}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreateForum(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creatingForum || !forumForm.name.trim()} className="btn-primary flex-1">{creatingForum ? 'Creating…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── New post modal ─────────────────────────────────────────────────── */}
      {showCreatePost && selectedForum && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">New post in {selectedForum.icon} {selectedForum.name}</h2>
              <button onClick={() => setShowCreatePost(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <form onSubmit={submitCreatePost} className="p-5 space-y-4">
              <input required className="input w-full" placeholder="Title — be specific"
                value={postForm.title} onChange={e => setPostForm(f => ({ ...f, title: e.target.value }))} />
              <textarea required className="input w-full resize-none h-32 text-sm" placeholder="Write your post… details help people answer."
                value={postForm.body} onChange={e => setPostForm(f => ({ ...f, body: e.target.value }))} />
              <input className="input w-full" placeholder="Tags (comma-sep: exams, hostel, dsa)"
                value={postForm.tagsInput} onChange={e => setPostForm(f => ({ ...f, tagsInput: e.target.value }))} />
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={postForm.isQuestion} onChange={e => setPostForm(f => ({ ...f, isQuestion: e.target.checked }))} className="accent-blue-600" />
                  <HelpCircle size={14} className="text-blue-500"/> This is a question
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={postForm.isAnonymous} onChange={e => setPostForm(f => ({ ...f, isAnonymous: e.target.checked }))} className="accent-indigo-600" />
                  Post anonymously
                </label>
              </div>
              {createPostErr && <p className="text-xs text-red-500">{createPostErr.message}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreatePost(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creatingPost || !postForm.title.trim() || !postForm.body.trim()} className="btn-primary flex-1">{creatingPost ? 'Posting…' : 'Post'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
